import type {
  Currency,
  ImportInput,
  ImportResult,
  LatestRate,
  LoginInput,
  PositionPoint,
  PositionSummary,
  RateHistory,
  RegisterInput,
  Share,
  ShareInput,
  SimulateInput,
  SimulateResult,
  TimingResponse,
  Transaction,
  TransactionInput,
  TransactionList,
  TransactionQuery,
  UpdateProfileInput,
  User,
} from '@cambio/shared';

/**
 * Typed HTTP client.
 *
 * The types come from `@cambio/shared`, the same zod schemas Fastify validates
 * against, so a contract change that only lands on one side of the wire is a
 * compile error here rather than a runtime surprise.
 *
 * Requests go to a same-origin `/api` prefix - Vite proxies it in development,
 * Caddy in production - which keeps the session cookie first-party in both.
 */

const BASE = '/api';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** The demo account rejecting a write, rather than a genuine permission bug. */
  get isReadOnly(): boolean {
    return this.status === 403;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const response = await fetch(`${BASE}${path}`, {
    method,
    // Session cookie rides along; there is no token to attach by hand.
    credentials: 'include',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text.length === 0 ? null : JSON.parse(text);

  if (!response.ok) {
    const envelope = payload as { error?: { code?: string; message?: string; details?: unknown } };
    throw new ApiRequestError(
      response.status,
      envelope?.error?.code ?? 'unknown',
      envelope?.error?.message ?? `请求失败 (${response.status})`,
      envelope?.error?.details,
    );
  }

  return payload as T;
}

/** Drops undefined so optional filters do not become `?side=undefined`. */
const query = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s.length === 0 ? '' : `?${s}`;
};

export const api = {
  auth: {
    register: (input: RegisterInput) => request<User>('/auth/register', { method: 'POST', body: input }),
    login: (input: LoginInput) => request<User>('/auth/login', { method: 'POST', body: input }),
    logout: () => request<void>('/auth/logout', { method: 'POST' }),
    me: (signal?: AbortSignal) => request<User>('/auth/me', signal ? { signal } : {}),
    updateProfile: (input: UpdateProfileInput) =>
      request<User>('/auth/me', { method: 'PATCH', body: input }),
    changePassword: (input: { currentPassword: string; newPassword: string }) =>
      request<void>('/auth/change-password', { method: 'POST', body: input }),
  },

  transactions: {
    list: (params: Partial<TransactionQuery> = {}) =>
      request<TransactionList>(`/transactions/${query(params as Record<string, string>)}`),
    create: (input: TransactionInput) =>
      request<Transaction>('/transactions/', { method: 'POST', body: input }),
    update: (id: string, input: Partial<TransactionInput>) =>
      request<Transaction>(`/transactions/${id}`, { method: 'PATCH', body: input }),
    remove: (id: string) => request<{ id: string }>(`/transactions/${id}`, { method: 'DELETE' }),
    restore: (id: string) =>
      request<Transaction>(`/transactions/${id}/restore`, { method: 'POST' }),
    import: (input: ImportInput) =>
      request<ImportResult>('/transactions/import', { method: 'POST', body: input }),
    /** Export is a plain link, not a fetch: the browser handles the download. */
    exportUrl: (format: 'csv' | 'json', quote?: string) =>
      `${BASE}/transactions/export${query({ format, quote })}`,
  },

  positions: {
    list: () => request<{ items: PositionSummary[] }>('/positions'),
    get: (quote: string, base?: string) =>
      request<PositionSummary>(`/positions/${quote}${query({ base })}`),
    series: (quote: string, base?: string) =>
      request<{ points: PositionPoint[] }>(`/positions/${quote}/series${query({ base })}`),
    simulate: (input: SimulateInput) =>
      request<SimulateResult>('/positions/simulate', { method: 'POST', body: input }),
    timing: (quote: string, params: { base?: string; from?: string; to?: string } = {}) =>
      request<TimingResponse>(`/positions/${quote}/timing${query(params)}`),
  },

  rates: {
    latest: (base: string, quote: string) =>
      request<LatestRate>(`/rates/latest${query({ base, quote })}`),
    history: (base: string, quote: string, from: string, to: string) =>
      request<RateHistory>(`/rates/history${query({ base, quote, from, to })}`),
  },

  shares: {
    list: () => request<{ items: Share[] }>('/shares'),
    create: (input: ShareInput) => request<Share>('/shares', { method: 'POST', body: input }),
    revoke: (id: string) => request<void>(`/shares/${id}`, { method: 'DELETE' }),
    public: (slug: string) =>
      request<{ displayName: string; scope: 'all' | 'pair'; items: PositionSummary[] }>(
        `/public/shares/${slug}`,
      ),
  },

  currencies: () => request<{ items: Currency[] }>('/currencies'),
};
